// Layer 2 - 2 years - Imitation, naming, simple matching (12 games total)
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "@/context/GameContext";
import { playSfx, speak, preloadAnimalSounds } from "@/lib/sounds";
import { t } from "@/lib/translations";

const playSfxHelper = (name, soundOn) => {
  try {
    playSfx(name, soundOn);
  } catch (e) {
    console.warn("Sound play failed", e);
  }
};

// ==========================================
// 1) Animal Sounds
// ==========================================
function AnimalSounds({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const animals = [
    { id: "cat", emoji: "🐱", sfx: "meow", name: { id: "Kucing", en: "Cat" } },
    { id: "dog", emoji: "🐶", sfx: "woof", name: { id: "Anjing", en: "Dog" } },
    { id: "cow", emoji: "🐮", sfx: "moo", name: { id: "Sapi", en: "Cow" } },
    { id: "duck", emoji: "🦆", sfx: "quack", name: { id: "Bebek", en: "Duck" } },
    { id: "sheep", emoji: "🐑", sfx: "baa", name: { id: "Domba", en: "Sheep" } },
    { id: "pig", emoji: "🐷", sfx: "oink", name: { id: "Babi", en: "Pig" } },
  ];
  const [tapped, setTapped] = useState({});

  useEffect(() => {
    preloadAnimalSounds();
  }, []);

  const tap = (a) => {
    playSfxHelper(a.sfx, soundOn);
    try {
      speak(a.name[language], language, soundOn);
    } catch (e) {
      console.warn("Speech failed", e);
    }
    const next = { ...tapped, [a.id]: true };
    setTapped(next);
    if (Object.keys(next).length >= 4) {
      setTimeout(() => onWin(), 1200);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-animals">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 mb-4 text-center">
        {t("what_sound", language)}
      </div>
      <div className="grid grid-cols-3 gap-4 max-w-2xl">
        {animals.map((a) => (
          <motion.button
            key={a.id}
            whileTap={{ scale: 0.88, rotate: [0, -6, 6, 0] }}
            onClick={() => tap(a)}
            className="rounded-3xl p-3 flex flex-col items-center bg-white border-4 border-yellow-200"
            style={{ boxShadow: "0 6px 0 #fcd34d" }}
            data-testid={`animal-${a.id}`}
          >
            <div className="text-6xl sm:text-7xl">{a.emoji}</div>
            <div className="font-display font-bold text-sm sm:text-base mt-1 text-slate-700">
              {a.name[language]}
            </div>
            {tapped[a.id] && <span className="text-green-500 text-xl font-bold">✓</span>}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ==========================================
// 2) Body Parts
// ==========================================
function BodyParts({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const parts = [
    { id: "head", emoji: "🧠", labelId: "Kepala", labelEn: "Head" },
    { id: "eyes", emoji: "👀", labelId: "Mata", labelEn: "Eyes" },
    { id: "nose", emoji: "👃", labelId: "Hidung", labelEn: "Nose" },
    { id: "mouth", emoji: "👄", labelId: "Mulut", labelEn: "Mouth" },
    { id: "hand", emoji: "✋", labelId: "Tangan", labelEn: "Hand" },
    { id: "foot", emoji: "🦶", labelId: "Kaki", labelEn: "Foot" },
  ];
  const [target, setTarget] = useState(() => parts[Math.floor(Math.random() * parts.length)]);
  const [score, setScore] = useState(0);
  const targetLabel = language === "id" ? target.labelId : target.labelEn;

  const pick = (p) => {
    if (p.id === target.id) {
      playSfxHelper("success", soundOn);
      const s = score + 1;
      setScore(s);
      if (s >= 3) {
        setTimeout(() => onWin(), 800);
        setScore(0);
      } else {
        setTarget(parts[Math.floor(Math.random() * parts.length)]);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-body">
      <div className="font-display text-2xl sm:text-3xl font-bold text-slate-700 mb-4 text-center">
        {t("show_part", language)}
        <span className="text-amber-500 ml-2">{targetLabel}</span>
      </div>
      <div className="grid grid-cols-3 gap-4 max-w-2xl">
        {parts.map((p) => (
          <motion.button
            key={p.id}
            whileTap={{ scale: 0.9 }}
            onClick={() => pick(p)}
            className="rounded-3xl bg-white p-4 border-4 border-yellow-200 flex flex-col items-center"
            style={{ boxShadow: "0 6px 0 #fcd34d" }}
            data-testid={`body-${p.id}`}
          >
            <div className="text-6xl">{p.emoji}</div>
            <div className="font-display font-bold text-sm mt-1 text-slate-700">
              {language === "id" ? p.labelId : p.labelEn}
            </div>
          </motion.button>
        ))}
      </div>
      <div className="mt-4 font-bold text-slate-500">⭐ {score}/3</div>
    </div>
  );
}

// ==========================================
// 3) Sort Shapes
// ==========================================
function SortShapes({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const shapes = [
    { id: "circle", emoji: "🔴", target: "circle" },
    { id: "square", emoji: "🟦", target: "square" },
    { id: "triangle", emoji: "🔺", target: "triangle" },
  ];
  const [placed, setPlaced] = useState({});
  const [selected, setSelected] = useState(null);

  const handlePlace = (boxId) => {
    if (!selected) return;
    if (selected.target === boxId) {
      playSfxHelper("success", soundOn);
      const next = { ...placed, [boxId]: selected.emoji };
      setPlaced(next);
      setSelected(null);
      if (Object.keys(next).length === 3) {
        setTimeout(() => onWin(), 800);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-sort">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 mb-3 text-center">
        {t("sort_to_box", language)}
      </div>
      <div className="flex gap-4 mb-6">
        {shapes.map((s) => (
          <button
            key={s.id}
            onClick={() => {
              playSfxHelper("tap", soundOn);
              setSelected(s);
            }}
            className="w-20 h-20 rounded-2xl bg-white border-4 flex items-center justify-center text-4xl"
            style={{
              borderColor: selected?.id === s.id ? "#EAB308" : "#fef08a",
              boxShadow: "0 5px 0 #fcd34d",
              opacity: placed[s.id] ? 0.3 : 1,
            }}
            disabled={!!placed[s.id]}
            data-testid={`shape-pick-${s.id}`}
          >
            {placed[s.id] ? "✓" : s.emoji}
          </button>
        ))}
      </div>
      <div className="flex gap-4">
        {shapes.map((s) => (
          <motion.button
            key={s.id}
            whileTap={{ scale: 0.95 }}
            onClick={() => handlePlace(s.id)}
            className="w-24 h-24 rounded-3xl border-4 border-dashed flex items-center justify-center text-4xl bg-white/60"
            style={{ borderColor: "#EAB308" }}
            data-testid={`shape-drop-${s.id}`}
          >
            {placed[s.id] || (s.id === "circle" ? "⚪" : s.id === "square" ? "⬜" : "△")}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ==========================================
// 4) Face Mimicry (Game 4)
// ==========================================
function FaceMimicry({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [step, setStep] = useState(0);
  const moods = [
    { emoji: "😄", id: "happy", name: { id: "Gembira", en: "Happy" }, sfx: "giggle" },
    { emoji: "😢", id: "sad", name: { id: "Sedih", en: "Sad" }, sfx: "wrong" },
    { emoji: "😮", id: "surprised", name: { id: "Terkejut", en: "Surprised" }, sfx: "pop" },
  ];
  const activeMood = moods[step % moods.length];

  const handleTap = () => {
    playSfxHelper(activeMood.sfx, soundOn);
    const nextStep = step + 1;
    setStep(nextStep);
    if (nextStep >= 3) {
      setTimeout(() => onWin(), 1000);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Tiru Wajah Maskot! 😆" : "Imitate the Mascot's Face! 😆"}
      </div>

      <motion.button
        onClick={handleTap}
        whileTap={{ scale: 0.9 }}
        className="w-48 h-48 sm:w-56 sm:h-56 rounded-full border-[8px] border-yellow-300 bg-white shadow-2xl flex items-center justify-center text-[8rem] select-none"
      >
        {activeMood.emoji}
      </motion.button>

      <div className="text-center font-display font-extrabold text-slate-600 text-lg uppercase tracking-wider">
        {activeMood.name[language]}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Merangsang Empati & Kognisi Emosional" : "Stimulates Empathy & Emotional Cognition"}
      </div>
    </div>
  );
}

// ==========================================
// 5) Blow the Candle (Game 5)
// ==========================================
function BlowCandle({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [taps, setTaps] = useState(0);
  const isBlown = taps >= 4;

  const handleTap = () => {
    if (isBlown) return;
    playSfxHelper("pop", soundOn);
    const newTaps = taps + 1;
    setTaps(newTaps);
    if (newTaps >= 4) {
      playSfxHelper("success", soundOn);
      setTimeout(() => onWin(), 1200);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Ketuk untuk Tiup Lilin! 🎂" : "Tap to Blow the Candle! 🎂"}
      </div>

      <motion.button
        onClick={handleTap}
        whileTap={{ scale: 0.95 }}
        className="w-56 h-56 sm:w-64 sm:h-64 rounded-3xl bg-white border-4 border-yellow-200 shadow-2xl flex flex-col items-center justify-center relative overflow-hidden"
      >
        <span className="text-[7rem] select-none">🎂</span>
        <span className="absolute text-5xl select-none top-4">
          {isBlown ? "💨" : "🔥"}
        </span>
      </motion.button>

      <div className="font-bold text-slate-500 text-sm">
        {isBlown
          ? (language === "id" ? "Hore! Lilin padam! 🎉" : "Yay! Candle is blown! 🎉")
          : (language === "id" ? `Ketuk ${4 - taps}x lagi untuk meniup` : `Tap ${4 - taps}x more to blow`)}
      </div>
    </div>
  );
}

// ==========================================
// 6) Toy Shadow Match (Game 6)
// ==========================================
function ToyShadowMatch({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handleMatch = () => {
    playSfxHelper("success", soundOn);
    setSuccess(true);
    setTimeout(() => onWin(), 1200);
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Cocokkan Bayangan Mainan! 🚗" : "Match the Toy Silhouette! 🚗"}
      </div>

      <div className="flex items-center gap-8 mt-6">
        {/* Silhouette Box */}
        <div className="w-32 h-32 rounded-3xl bg-slate-300 border-4 border-dashed border-slate-400 flex items-center justify-center text-7xl select-none">
          <span className={success ? "opacity-100 transition-opacity" : "opacity-0 grayscale brightness-0"}>
            🚗
          </span>
        </div>

        <span className="text-4xl text-slate-400 font-bold">➡️</span>

        {/* Playable Object */}
        <motion.button
          onClick={handleMatch}
          disabled={success}
          whileTap={{ scale: 0.9 }}
          className="w-32 h-32 rounded-3xl bg-white border-4 border-yellow-300 shadow-xl flex items-center justify-center text-7xl select-none"
        >
          🚗
        </motion.button>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Pengenalan Bentuk & Asosiasi Spasial" : "Trains Shape Recognition & Spatial Association"}
      </div>
    </div>
  );
}

// ==========================================
// 7) Fruit Basket (Game 7)
// ==========================================
function FruitBasket({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [basket, setBasket] = useState([]);
  const fruits = [
    { id: 1, emoji: "🍎", name: "Apel", color: "red" },
    { id: 2, emoji: "🍌", name: "Pisang", color: "yellow" },
    { id: 3, emoji: "🍓", name: "Stroberi", color: "red" },
  ];

  const handleTap = (fr) => {
    playSfxHelper("pop", soundOn);
    setBasket((prev) => {
      const next = [...prev, fr.id];
      if (next.length === fruits.length) {
        setTimeout(() => onWin(), 1000);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Masukkan Buah ke Keranjang! 🧺" : "Put Fruits into Basket! 🧺"} ({basket.length}/3)
      </div>

      <div className="flex gap-6 justify-center">
        {fruits.map((fr) => {
          const inside = basket.includes(fr.id);
          return (
            <motion.button
              key={fr.id}
              onClick={() => handleTap(fr)}
              disabled={inside}
              animate={inside ? { y: 150, opacity: 0 } : {}}
              className="w-24 h-24 rounded-2xl bg-white border-4 border-yellow-100 flex items-center justify-center text-5xl shadow-lg"
            >
              {fr.emoji}
            </motion.button>
          );
        })}
      </div>

      <div className="w-56 h-28 border-t-8 border-yellow-700 rounded-b-3xl bg-amber-500 flex items-center justify-center text-5xl shadow-xl mt-6 relative">
        <span className="text-white font-display font-black text-xl uppercase tracking-wider">
          {language === "id" ? "Keranjang" : "Basket"}
        </span>
      </div>
    </div>
  );
}

// ==========================================
// 8) Toy Phone Call (Game 8)
// ==========================================
function ToyPhoneCall({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [called, setCalled] = useState(false);
  const [ended, setEnded] = useState(false);

  const answerCall = () => {
    playSfxHelper("success", soundOn);
    setCalled(true);
    // Simulate short speech
    setTimeout(() => {
    try {
      speak(language === "id" ? "Halo! Apa kabar?" : "Hello! How are you?", language, soundOn);
    } catch (e) {
      console.warn("Speech failed", e);
    }
    }, 500);
  };

  const endCall = () => {
    playSfxHelper("pop", soundOn);
    setEnded(true);
    setTimeout(() => onWin(), 800);
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Telepon Mainan Bintang! ☎️" : "Star Toy Phone! ☎️"}
      </div>

      <div className="w-56 h-56 rounded-full bg-white border-8 border-yellow-300 shadow-2xl flex flex-col items-center justify-center gap-4">
        <span className="text-6xl animate-bounce">☎️</span>
        {called && !ended && (
          <span className="font-display font-extrabold text-green-600 text-sm animate-pulse">
            {language === "id" ? "Bicara..." : "Talking..."}
          </span>
        )}
      </div>

      <div className="flex gap-4">
        {!called ? (
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={answerCall}
            className="px-6 py-3 rounded-2xl bg-emerald-500 text-white font-display font-black text-lg shadow-lg"
          >
            📞 {language === "id" ? "Jawab" : "Answer"}
          </motion.button>
        ) : (
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={endCall}
            className="px-6 py-3 rounded-2xl bg-red-500 text-white font-display font-black text-lg shadow-lg"
          >
            ❌ {language === "id" ? "Tutup" : "Hang Up"}
          </motion.button>
        )}
      </div>
    </div>
  );
}

// ==========================================
// 9) Balloon Float (Game 9)
// ==========================================
function BalloonFloat({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [taps, setTaps] = useState(0);
  const [position, setPosition] = useState({ x: 40, y: 40 });

  const bounce = () => {
    playSfxHelper("pop", soundOn);
    const next = taps + 1;
    setTaps(next);
    // Relocate upward
    setPosition({
      x: 10 + Math.random() * 70,
      y: 10 + Math.random() * 30,
    });
    if (next >= 4) {
      setTimeout(() => onWin(), 600);
    }
  };

  useEffect(() => {
    // Keep drifting downward
    const timer = setInterval(() => {
      setPosition((pos) => ({
        ...pos,
        y: Math.min(pos.y + 3, 75),
      }));
    }, 150);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative w-full rounded-3xl bg-gradient-to-b from-sky-100 to-sky-50 min-h-[60vh] overflow-hidden border-4 border-white flex flex-col justify-between p-4">
      <div className="text-center font-display font-bold text-lg sm:text-xl text-sky-800">
        {language === "id" ? "Jaga Balon Tetap Melayang! 🎈" : "Keep the Balloon Floating! 🎈"} ({taps}/4)
      </div>

      <motion.button
        onClick={bounce}
        style={{
          left: `${position.x}%`,
          top: `${position.y}%`,
        }}
        className="absolute w-24 h-24 rounded-full flex items-center justify-center select-none"
      >
        <span className="text-[5.5rem]">🎈</span>
      </motion.button>

      <div className="text-center text-xs font-bold text-sky-500">
        {language === "id" ? "Latih Antisipasi Gerakan & Refleks" : "Trains Action Anticipation & Reflexes"}
      </div>
    </div>
  );
}

// ==========================================
// 10) Toy Hammering (Game 10)
// ==========================================
function ToyHammering({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [pegs, setPegs] = useState([false, false, false]);

  const hammerPeg = (index) => {
    playSfxHelper("woof", soundOn); // substitute for hammer bang sound
    setPegs((prev) => {
      const next = [...prev];
      next[index] = true;
      if (next.every((p) => p)) {
        setTimeout(() => onWin(), 800);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Pukul Pasak Kayu! 🔨" : "Hammer the Pegs! 🔨"}
      </div>

      <div className="flex gap-6 p-6 rounded-3xl bg-amber-100 border-4 border-yellow-400 shadow-xl items-end min-h-[12rem]">
        {pegs.map((isFlat, i) => (
          <motion.button
            key={i}
            onClick={() => hammerPeg(i)}
            disabled={isFlat}
            animate={isFlat ? { height: 35 } : { height: 100 }}
            className="w-12 rounded-t-xl bg-orange-500 border-x-4 border-t-4 border-orange-600 shadow-md"
            style={{ minHeight: "20px" }}
          />
        ))}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Mengembangkan Integrasi Motorik-Sensorik" : "Develops Perceptual-Motor Integration"}
      </div>
    </div>
  );
}

// ==========================================
// 11) Donut Stacking (Game 11)
// ==========================================
function DonutStacking({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [stacked, setStacked] = useState([]);
  const donuts = [
    { size: "w-48 h-10", color: "bg-red-400 border-red-500", id: 1 },
    { size: "w-36 h-10", color: "bg-yellow-400 border-yellow-500", id: 2 },
    { size: "w-24 h-10", color: "bg-sky-400 border-sky-500", id: 3 },
  ];

  const handleDonutClick = (id) => {
    const nextExpected = stacked.length + 1;
    if (id === nextExpected) {
      playSfxHelper("success", soundOn);
      const nextStack = [...stacked, id];
      setStacked(nextStack);
      if (nextStack.length === donuts.length) {
        setTimeout(() => onWin(), 1000);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Susun Donat dari Besar ke Kecil! 🍩" : "Stack Donuts Large to Small! 🍩"}
      </div>

      {/* Stack Base / Pole */}
      <div className="relative w-64 h-56 flex flex-col justify-end items-center">
        <div className="absolute bottom-0 w-56 h-4 bg-amber-800 rounded-full" />
        <div className="absolute bottom-0 w-6 h-48 bg-amber-700 rounded-t-full shadow-inner" />

        {/* Stacked donuts */}
        <div className="flex flex-col-reverse items-center z-10">
          {donuts.map((d) => {
            const isStacked = stacked.includes(d.id);
            if (!isStacked) return null;
            return (
              <motion.div
                key={d.id}
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                className={`${d.size} ${d.color} rounded-full border-4 shadow-md flex items-center justify-center`}
              />
            );
          })}
        </div>
      </div>

      {/* Available items to click */}
      <div className="flex gap-4 items-center">
        {donuts.map((d) => {
          const isStacked = stacked.includes(d.id);
          if (isStacked) return null;
          return (
            <motion.button
              key={d.id}
              onClick={() => handleDonutClick(d.id)}
              whileTap={{ scale: 0.9 }}
              className={`px-4 py-2 ${d.color} text-white font-display font-black rounded-2xl shadow-lg border-2`}
            >
              {d.id === 1 ? (language === "id" ? "Besar" : "Large") : ""}
              {d.id === 2 ? (language === "id" ? "Sedang" : "Medium") : ""}
              {d.id === 3 ? (language === "id" ? "Kecil" : "Small") : ""}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// ==========================================
// 12) Sand Sweeper (Game 12)
// ==========================================
function SandSweeper({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [revealed, setRevealed] = useState(false);
  const [sandPatches, setSandPatches] = useState(() =>
    Array.from({ length: 9 }, (_, i) => ({
      id: i,
      wiped: false,
    }))
  );

  const sweepSand = (id) => {
    if (revealed) return;
    playSfxHelper("pop", soundOn);
    setSandPatches((prev) => {
      const next = prev.map((p) => (p.id === id ? { ...p, wiped: true } : p));
      const wipedCount = next.filter((p) => p.wiped).length;
      if (wipedCount === prev.length) {
        setRevealed(true);
        setTimeout(() => onWin(), 1200);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Usap Pasir Pantai! 🏖️" : "Sweep the Beach Sand! 🏖️"}
      </div>

      <div className="relative w-64 h-64 sm:w-80 sm:h-80 rounded-3xl overflow-hidden border-4 border-yellow-200 shadow-2xl bg-sky-200 flex items-center justify-center">
        <motion.div
          animate={revealed ? { scale: [1, 1.25, 1], rotate: [0, 15, -15, 0] } : {}}
          transition={{ duration: 1 }}
          className="text-9xl select-none"
        >
          🐚
        </motion.div>

        {!revealed && (
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-0.5 bg-yellow-600/40">
            {sandPatches.map((p) => (
              <motion.button
                key={p.id}
                onMouseEnter={() => sweepSand(p.id)}
                onTouchStart={() => sweepSand(p.id)}
                onClick={() => sweepSand(p.id)}
                animate={{ opacity: p.wiped ? 0 : 1 }}
                className="w-full h-full bg-yellow-200 border border-yellow-300/40 flex items-center justify-center text-4xl select-none"
              >
                🏜️
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Rangsangan Sensori Haptic & Eksplorasi Jari" : "Haptic Sensory Stimulation & Finger Exploration"}
      </div>
    </div>
  );
}

// Export Layer 2 games list
export const Layer2Games = {
  animals: { icon: "🐱", Component: AnimalSounds },
  body: { icon: "👀", Component: BodyParts },
  sort: { icon: "🔺", Component: SortShapes },
  mimicry: { icon: "😄", Component: FaceMimicry },
  blow: { icon: "🎂", Component: BlowCandle },
  toy_match: { icon: "🚗", Component: ToyShadowMatch },
  fruit_basket: { icon: "🍎", Component: FruitBasket },
  toy_phone: { icon: "☎️", Component: ToyPhoneCall },
  balloon_float: { icon: "🎈", Component: BalloonFloat },
  hammering: { icon: "🔨", Component: ToyHammering },
  donut_stack: { icon: "🍩", Component: DonutStacking },
  sand_sweeper: { icon: "🏖️", Component: SandSweeper },
};
